import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { encryptPassword, getBrowserInfo, getOsInfo } from '@/lib/crypto';
import { renderGoogleButton } from '@/lib/google';

type LoginStep = 'email' | 'password';

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const LoginPage = () => {
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [publicKey, setPublicKey] = useState('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { completeLogin, consumeRedirect, isAuthenticated } = useAuth();
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/account', { replace: true });
      return;
    }
    void api
      .getWebsiteInfo()
      .then((website) => setPublicKey(website.rsaLoginPublicKey || ''))
      .catch((requestError) => {
        setError(requestError instanceof Error ? requestError.message : '初始化失敗');
      });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (step !== 'email' || !googleButtonRef.current || isAuthenticated) {
      return;
    }

    let active = true;
    setGoogleLoading(true);
    void renderGoogleButton({
      container: googleButtonRef.current,
      text: 'signin_with',
      onCredential: async (googleToken) => {
        if (!active) return;
        setGoogleLoading(true);
        setError('');
        try {
          const result = await api.googleLogin({ googleToken });
          await completeLogin(result);
          const redirect = searchParams.get('redirect') || consumeRedirect() || '/account';
          navigate(redirect, { replace: true });
        } catch (requestError) {
          setError(requestError instanceof Error ? requestError.message : 'Google 登入失敗');
          setGoogleLoading(false);
        }
      },
      onError: (requestError) => {
        if (!active) return;
        setError(requestError.message);
        setGoogleLoading(false);
      }
    })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError instanceof Error ? requestError.message : 'Google 登入初始化失敗');
      })
      .finally(() => {
        if (active) setGoogleLoading(false);
      });

    return () => {
      active = false;
    };
  }, [completeLogin, consumeRedirect, isAuthenticated, navigate, searchParams, step]);

  const handleContinue = () => {
    const value = email.trim();
    if (!value) {
      setError('請輸入帳號（Email）');
      return;
    }
    if (!isValidEmail(value)) {
      setError('Email 格式不正確');
      return;
    }
    setEmail(value);
    setError('');
    setStep('password');
  };

  const handlePasswordLogin = (e: React.FormEvent) => {
    e.preventDefault();
    void (async () => {
      if (!password) {
        setError('請輸入密碼');
        return;
      }
      if (!publicKey) {
        setError('系統尚未完成初始化，請稍後重試');
        return;
      }

      setLoading(true);
      setError('');
      try {
        const mobilePwdEncrypt = encryptPassword(password, publicKey);
        const result = await api.login({
          email,
          mobilePwdEncrypt,
          os: getOsInfo(),
          browser: getBrowserInfo().name
        });
        await completeLogin(result);
        const redirect = searchParams.get('redirect') || consumeRedirect() || '/account';
        navigate(redirect, { replace: true });
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : '登入失敗');
      } finally {
        setLoading(false);
      }
    })();
  };

  return (
    <div className="relative min-h-screen bg-[radial-gradient(circle_at_top,_#e2e8f0_0%,_#f8fafc_38%,_#eef2ff_100%)] px-4 py-10 sm:px-6 sm:py-14">
      <div className="absolute left-4 top-4 sm:left-7 sm:top-6">
        <Link to="/" className="inline-flex items-center gap-3 rounded-2xl border border-white/70 bg-white/80 px-4 py-2 backdrop-blur-md shadow-sm">
          <img
            src="/assets/readdy/static-358c7e6691b9b58b5179c58a2715558b.jpg"
            alt="NexusGo"
            className="h-9 w-9 rounded-xl object-cover"
          />
          <span className="text-base font-bold tracking-tight text-[#0A1628]">NexusGo</span>
        </Link>
      </div>

      <div className="mx-auto mt-20 w-full max-w-[540px]">
        <div className="overflow-hidden rounded-[28px] border border-white/70 bg-white/90 p-6 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)] backdrop-blur-xl sm:p-8">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-black text-slate-900">登入 NexusGo</h1>
            <p className="mt-1 text-sm text-slate-500">
              還沒有帳號？{' '}
              <Link to="/register" className="font-semibold text-violet-600 hover:text-violet-700">
                建立帳號
              </Link>
            </p>
          </div>

          {error ? (
            <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
              <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
                <i className="ri-information-line text-sm text-amber-500" />
              </div>
              <div className="text-xs leading-relaxed text-amber-800">{error}</div>
            </div>
          ) : null}

          {step === 'email' ? (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">帳號（Email）</label>
              <input
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (error) setError('');
                }}
                placeholder="name@email.com"
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
              />

              <button
                type="button"
                onClick={handleContinue}
                className="mt-4 w-full rounded-xl bg-[#0A1628] py-3 text-sm font-semibold text-white transition-all hover:bg-[#12233f]"
              >
                繼續
              </button>

              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-100" />
                <span className="text-xs text-slate-400">使用 Google 登入</span>
                <div className="h-px flex-1 bg-slate-100" />
              </div>

              <div
                ref={googleButtonRef}
                className={`min-h-11 transition-opacity ${googleLoading ? 'opacity-70' : 'opacity-100'}`}
              />
              {googleLoading ? (
                <div className="mt-2 flex items-center justify-center gap-2 text-xs text-slate-500">
                  <i className="ri-loader-4-line animate-spin" />
                  正在初始化 Google 登入
                </div>
              ) : null}
            </div>
          ) : (
            <form onSubmit={handlePasswordLogin}>
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setPassword('');
                  setShowPassword(false);
                  setError('');
                }}
                className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
              >
                <i className="ri-arrow-left-line" />
                返回
              </button>

              <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {email}
              </div>

              <label className="mb-1.5 block text-sm font-medium text-slate-700">輸入密碼</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (error) setError('');
                  }}
                  placeholder="你的密碼"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-slate-400 hover:text-slate-600"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-4 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-violet-600 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    登入中...
                  </>
                ) : (
                  '登入'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
